import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector, NgZone, runInInjectionContext } from '@angular/core';
import { Messaging, getToken, onMessage, getMessaging as _getMessaging } from '@angular/fire/messaging';
import { environment } from '../../../environments/environment';
import { Platform } from '@angular/cdk/platform';
import { Subject, BehaviorSubject, Observable, timer, EMPTY } from 'rxjs';
import { initializeApp } from '@angular/fire/app';
import { switchMap, retry, share } from 'rxjs/operators';
import { WebSocketService } from '../websocket/websocket.service';

export interface NotificationResponse {
  status: boolean;
  code: number;
  result: Notification[];
  lastPage: number;
  loadmore: string;
  err: any;
}

export interface Notification {
  id: number;
  user_id: number;
  type: string;
  action_id: number;
  action_by_user_id: number;
  is_notified: string;
  is_read: string;
  created_at: string;
  updated_at: string;
  profile_img: string;
  from_username: string;
  to_username: string;
  event_name: string;
  event_slug: string;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  private deviceToken: string | null = null;
  private isSupported: boolean = false;
  private messagingInstance: Messaging | null = null;
  private readonly POLLING_INTERVAL = 30000; // 30 seconds

  public notificationReceived$ = new Subject<any>();
  public isInitialized$ = new BehaviorSubject<boolean>(false);
  public notifications$ = new BehaviorSubject<Notification[]>([]);
  public unreadCount$ = new BehaviorSubject<number>(0);

  private pollingSubscription: any;
  private notificationSubscription: any = null;
  private isWebSocketSubscribed = false;

  constructor(
    private http: HttpClient,
    private zone: NgZone,
    private injector: Injector,
    private platform: Platform,
    private websocketService: WebSocketService
  ) {
    this.checkSupport();
    this.setupServiceWorkerMessageListener();
    if (this.isAuthenticated()) {
      this.subscribeToNotifications();
      this.loadInitialNotifications();
    }
  }

  private checkSupport() {
    this.isSupported = !this.platform.IOS && !this.platform.SAFARI && 'Notification' in window;
  }

  // Helper to get Messaging only if supported and provided
  private getMessaging(): Messaging | null {
    if (!this.isSupported) {
      return null;
    }

    if (this.messagingInstance) {
      return this.messagingInstance;
    }

    try {
      return runInInjectionContext(this.injector, () => {
        if (!this.messagingInstance) {
          const app = initializeApp(environment.firebaseConfig);
          this.messagingInstance = _getMessaging(app);
        }
        return this.messagingInstance;
      });
    } catch (error) {
      console.error('Error getting messaging instance:', error);
      return null;
    }
  }

  private isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('user_id');
    return !!(token && userId);
  }

  private startPollingIfAuthenticated() {
    // This method is kept for backward compatibility but polling is replaced with WebSocket
    if (this.isAuthenticated()) {
      // Polling disabled - using WebSocket instead
    }
  }

  /**
   * Public method to start WebSocket subscription after authentication
   * Should be called after successful login
   */
  startPollingAfterAuth() {
    if (!this.isWebSocketSubscribed) {
      this.subscribeToNotifications();
      this.loadInitialNotifications();
    }
  }

  private startPolling() {
    // Create an observable that emits every POLLING_INTERVAL milliseconds
    this.pollingSubscription = timer(0, this.POLLING_INTERVAL).pipe(
      switchMap(() => {
        // Check authentication before each API call
        if (!this.isAuthenticated()) {
          // Stop polling if user is not authenticated
          this.stopPolling();
          return EMPTY;
        }
        return this.getNotifications();
      }),
      retry({
        delay: 5000 // Retry after 5 seconds if there's an error
      }),
      share()
    ).subscribe({
      next: (response: any) => {
        if (response && response.result) {
          this.notifications$.next(response.result);
          const unreadCount = response.result.filter((n: Notification) => n.is_read === 'n').length;
          this.unreadCount$.next(unreadCount);
        }
      },
      error: (error) => {
        // Error handled silently
      }
    });
  }

  stopPolling() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  private subscribeToNotifications(): void {
    if (!this.isAuthenticated() || this.isWebSocketSubscribed) {
      return;
    }

    const userId = localStorage.getItem('user_id');
    if (!userId) {
      return;
    }

    try {
      this.websocketService.subscribeToNotifications(userId);
      this.isWebSocketSubscribed = true;

      this.notificationSubscription = this.websocketService.onNotification().subscribe({
        next: (notification: any) => {
          this.handleNewNotification(notification);
        },
        error: (error) => {
          // Error handled silently
        }
      });
    } catch (error) {
      // Error handled silently
    }
  }

  private handleNewNotification(notification: any): void {
    const newNotification: Notification = {
      id: notification.id,
      user_id: notification.user_id,
      type: notification.type,
      action_id: notification.action_id,
      action_by_user_id: notification.action_by_user_id,
      is_notified: notification.is_notified || 'n',
      is_read: notification.is_read || 'n',
      created_at: notification.created_at,
      updated_at: notification.updated_at,
      profile_img: notification.profile_img || '',
      from_username: notification.from_username || '',
      to_username: notification.to_username || '',
      event_name: notification.event_name || '',
      event_slug: notification.event_slug || '',
      message: notification.message || '',
    };

    // Prevent duplicates
    const currentNotifications = this.notifications$.value;
    if (currentNotifications.some(n => n.id === newNotification.id)) {
      return;
    }

    // Prepend new notification
    this.notifications$.next([newNotification, ...currentNotifications]);

    // Update unread count
    if (newNotification.is_read === 'n') {
      this.unreadCount$.next(this.unreadCount$.value + 1);
    }

    // Optionally trigger browser notification
    if (Notification.permission === 'granted' && document.hidden) {
      this.showBrowserNotification(newNotification);
    }
  }

  private showBrowserNotification(notification: Notification): void {
    if ('Notification' in window) {
      try {
        new Notification(notification.message || 'New Notification', {
          icon: notification.profile_img || '/assets/icons/icon-72x72.png',
          tag: `notification-${notification.id}`,
        });
      } catch (error) {
        // Error handled silently
      }
    }
  }

  private loadInitialNotifications(): void {
    this.getNotifications(1).subscribe({
      next: (response: NotificationResponse) => {
        if (response && response.result) {
          this.notifications$.next(response.result);
          const unreadCount = response.result.filter((n: Notification) => n.is_read === 'n').length;
          this.unreadCount$.next(unreadCount);
        }
      },
      error: (error) => {
        // Error handled silently
      }
    });
  }

  private unsubscribeFromNotifications(): void {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
      this.notificationSubscription = null;
    }
    this.websocketService.unsubscribeFromNotifications();
    this.isWebSocketSubscribed = false;
  }

  getNotifications(page: number = 1): Observable<NotificationResponse> {
    // Check authentication before making API call
    if (!this.isAuthenticated()) {
      // Return empty observable if not authenticated
      return new Observable<NotificationResponse>(observer => {
        observer.complete();
      });
    }

    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    const payload = {
      userid: localStorage.getItem('user_id'),
      page: page,
      limit: 8
    };

    return this.http.post<NotificationResponse>(
      `${environment.apiUrl}/getAllNotification`,
      payload,
      { headers }
    );
  }

  async markAllNotificationsAsRead() {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    const payload = {
      user_id : localStorage.getItem('user_id')
    };

    try {
      await this.http.post(`${environment.apiUrl}/userReadAllNotification`, payload, { headers })
        .toPromise();

      // Update local state
      const currentNotifications = this.notifications$.value;
      const updatedNotifications = currentNotifications.map(n => ({ ...n, is_read: 'y' }));
      this.notifications$.next(updatedNotifications);
      this.unreadCount$.next(0);
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      throw error;
    }
  }

  async markNotificationAsRead(notificationId: number) {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    const payload = {
      user_id: localStorage.getItem('user_id'),
      notification_id: notificationId
    };

    try {
      await this.http.post(`${environment.apiUrl}/userReadNotification`, payload, { headers })
        .toPromise();

      // Update local state
      const currentNotifications = this.notifications$.value;
      const updatedNotifications = currentNotifications.map(n =>
        n.id === notificationId ? { ...n, is_read: 'y' } : n
      );
      this.notifications$.next(updatedNotifications);

      // Update unread count
      const unreadCount = updatedNotifications.filter(n => n.is_read === 'n').length;
      this.unreadCount$.next(unreadCount);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  notificationSettings(
    chat_notification: string,
    email_notification: string,
    event_notification: string
  ) {
    const payload = {
      user_id: localStorage.getItem('user_id'),
      chat_notification,
      email_notification,
      event_notification
    };

    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    return this.http.post(environment.apiUrl + '/updateNotificationSettings', payload, {
      headers
    });
  }

  // This method should be called from a user interaction (like button click)
  async initializeNotifications() {
    if (!this.isSupported) {
      return;
    }

    try {
      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        const token = await this.fetchDeviceToken();
        if (token) {
          await this.setupMessageListener();
        }
      } else {
        console.warn('Notification permission not granted');
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
    }
  }

  private setupServiceWorkerMessageListener() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'PUSH_NOTIFICATION') {
          this.zone.run(() => {
            this.handleNotificationPayload(event.data.payload);
          });
        } else if (event.data.type === 'NOTIFICATION_CLICKED') {
          this.zone.run(() => {
            this.handleNotificationClick(event.data.payload);
          });
        }
      });
    }
  }

  private handleNotificationClick(payload: any) {
    // Handle the click based on the payload data
    const clickAction = payload?.click_action || 'https://theselfwish.com';

    // You can implement custom navigation logic here
    if (clickAction.startsWith('https://theselfwish.com')) {
      // Internal navigation - extract the path
      const path = new URL(clickAction).pathname;
      // You can use Router.navigate here if needed
      window.location.href = clickAction;
    } else {
      // External link
      window.open(clickAction, '_blank');
    }
  }

  private handleNotificationPayload(payload: any) {
    try {
      const notificationData = {
        title: payload.notification?.title || (payload.data && payload.data['title']) || 'New Notification',
        body: payload.notification?.body || (payload.data && payload.data['body']) || '',
        timestamp: new Date(),
        data: payload.data || {},
        link: payload.fcmOptions?.link || (payload.data && payload.data['click_action']) || 'https://theselfwish.com'
      };

      this.notificationReceived$.next(notificationData);

      // Show browser notification if in background
      if (document.hidden && Notification.permission === 'granted') {
        const notification = new Notification(notificationData.title, {
          body: notificationData.body,
          icon: '/assets/icons/icon-72x72.png',
          tag: 'notification-1',
          data: {
            ...notificationData.data,
            click_action: notificationData.link
          }
        });

        // Handle notification click in foreground
        notification.onclick = () => {
          this.handleNotificationClick({
            click_action: notificationData.link,
            ...notificationData.data
          });
        };
      }
    } catch (error) {
      // Error handled silently
    }
  }

  private async setupMessageListener() {
    const messaging = this.getMessaging();
    if (!messaging) {
      console.error('No messaging instance available');
      return;
    }

    try {
      runInInjectionContext(this.injector, () => {
        onMessage(messaging, (payload) => {
          this.zone.run(() => {
            this.handleNotificationPayload(payload);
          });
        });
      });

      this.isInitialized$.next(true);
    } catch (error) {
      console.error('Error setting up message listener:', error);
      this.isInitialized$.next(false);
    }
  }

  async fetchDeviceToken(): Promise<string | null> {
    if (!this.isSupported) {
      return null;
    }

    const messaging = this.getMessaging();
    if (!messaging) {
      console.error('No messaging instance available');
      return null;
    }

    try {
      const token = await runInInjectionContext(this.injector, () =>
        getToken(messaging, {
          vapidKey: environment.firebaseConfig.vapidKey
        })
      );

      if (token) {
        this.deviceToken = token;
        return token;
      } else {
        console.warn('No token generated');
        return null;
      }
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  }

  ngOnDestroy() {
    this.stopPolling();
    this.unsubscribeFromNotifications();
  }
}
